import i18next from "i18next";
import { toast } from "sonner";

import { useUserStore } from "@/modules/auth";
import { lockPrivacySession } from "@/modules/mine/privacy/session";
import { getActivePrivacyToken, usePrivacySessionStore } from "@/modules/mine/privacy/store";
import { registerApiRuntime } from "@/shared/api/client";
import { getErrorPresentation } from "@/shared/api/error-presentation";
import { HTTP_STATUS_CODE, INTERNAL_ERROR_CODE } from "@/shared/constants/http";
import type { UnifiedError } from "@/shared/types/api";

function resolveErrorMessage(error: UnifiedError): string {
  if (error.status === HTTP_STATUS_CODE.BAD_REQUEST && error.reason) {
    return i18next.t(error.reason, {
      ns: "errors",
      defaultValue: error.message || i18next.t("REQUEST_FAILED", { ns: "errors" }),
    });
  }

  if (error.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
    return i18next.t("UNAUTHORIZED", { ns: "errors" });
  }

  if (error.status && error.status >= HTTP_STATUS_CODE.SERVER_ERROR) {
    return i18next.t("SERVER_ERROR", { ns: "errors" });
  }

  if (error.code === INTERNAL_ERROR_CODE.TIMEOUT) {
    return i18next.t("REQUEST_TIMEOUT", { ns: "errors" });
  }

  if (error.code === INTERNAL_ERROR_CODE.NETWORK) {
    return i18next.t("NETWORK_ERROR", { ns: "errors" });
  }

  return error.message || i18next.t("REQUEST_FAILED", { ns: "errors" });
}

registerApiRuntime({
  getToken: () => useUserStore.getState().token,
  getPrivacyToken: getActivePrivacyToken,
  getLanguage: () => i18next.language,
  onUnauthorized: () => {
    lockPrivacySession({ refetch: false });
    useUserStore.getState().actions.logout();
  },
  onError: (error, context) => {
    if (error.reason === "PRIVACY_LOCKED") {
      if (usePrivacySessionStore.getState().token) {
        lockPrivacySession();
        toast.info("私密内容已锁定，请重新解锁");
      }
      return;
    }
    if (context.silent) {
      return;
    }

    const { title, description } = getErrorPresentation({
      ...error,
      message: resolveErrorMessage(error),
    });

    toast.error(title, {
      id: `${error.status ?? "unknown"}-${error.reason ?? error.code}`,
      description,
    });
  },
});
